import { FFmpeg } from '@ffmpeg/ffmpeg';
import { toBlobURL, fetchFile } from '@ffmpeg/util';

let ffmpeg = null;
let ffmpegLoaded = false;
let loadingPromise = null;

/**
 * Generate a unique filename for the compressed video
 */
const generateFileName = () => {
  return `low_res_${Date.now()}.mp4`;
};

/**
 * Initialize FFmpeg (loads WASM files)
 * This should be called once before using compression
 */
export const initFFmpeg = async (onProgress) => {
  if (ffmpegLoaded && ffmpeg) {
    return ffmpeg;
  }

  // If already loading, wait for it
  if (loadingPromise) {
    return loadingPromise;
  }

  loadingPromise = (async () => {
    try {
      ffmpeg = new FFmpeg();

      // Set up progress handler
      if (onProgress) {
        ffmpeg.on('progress', ({ progress }) => {
          onProgress(Math.round(progress * 100));
        });
      }

      // Load FFmpeg with CORS-enabled URLs from unpkg CDN
      const baseURL = 'https://unpkg.com/@ffmpeg/core@0.12.6/dist/umd';
      
      await ffmpeg.load({
        coreURL: await toBlobURL(`${baseURL}/ffmpeg-core.js`, 'text/javascript'),
        wasmURL: await toBlobURL(`${baseURL}/ffmpeg-core.wasm`, 'application/wasm'),
      });

      ffmpegLoaded = true;
      return ffmpeg;
    } catch (error) {
      console.error('Failed to load FFmpeg:', error);
      loadingPromise = null;
      throw error;
    }
  })();

  return loadingPromise;
};

/**
 * Compress video with optimized settings
 * @param {File} videoFile - Original video file
 * @param {Function} onProgress - Progress callback (0-100)
 * @returns {Promise<File>} - Compressed video file
 */
export const compressVideoToLowRes = async (videoFile, onProgress) => {
  try {
    // Initialize FFmpeg if not already done
    const ff = await initFFmpeg(onProgress);

    const inputFileName = 'input.mp4';
    const outputFileName = 'output_low_res.mp4';

    // Write the input file to FFmpeg's virtual filesystem
    const videoData = await fetchFile(videoFile);
    await ff.writeFile(inputFileName, videoData);

    // Run FFmpeg compression command with optimized settings
    // -c:v libx264 : Use H.264 codec
    // -preset medium : Good balance of speed and quality
    // -b:v 1500k : Target video bitrate 1500kbps
    // -maxrate 2000k : Max bitrate for VBV
    // -bufsize 3000k : VBV buffer size
    // -c:a aac : AAC audio codec
    // -b:a 128k : Audio bitrate
    // -movflags +faststart : Enable progressive download
    await ff.exec([
      '-i', inputFileName,
      '-c:v', 'libx264',
      '-preset', 'medium',
      '-b:v', '1500k',
      '-maxrate', '2000k',
      '-bufsize', '3000k',
      '-c:a', 'aac',
      '-b:a', '128k',
      '-movflags', '+faststart',
      '-y',
      outputFileName
    ]);

    // Read the output file
    const outputData = await ff.readFile(outputFileName);
    
    // Clean up
    await ff.deleteFile(inputFileName);
    await ff.deleteFile(outputFileName);

    // Create a new File from the compressed data
    const compressedBlob = new Blob([outputData], { type: 'video/mp4' });
    const compressedFile = new File(
      [compressedBlob], 
      `low_res_${videoFile.name.replace(/\.[^/.]+$/, '')}.mp4`,
      { type: 'video/mp4' }
    );

    return compressedFile;
  } catch (error) {
    console.error('Video compression failed:', error);
    throw error;
  }
};

/**
 * Check if FFmpeg is supported in the current browser
 */
export const isFFmpegSupported = () => {
  return typeof SharedArrayBuffer !== 'undefined';
};

/**
 * Compress video from URL (for existing videos)
 * @param {string} videoUrl - URL of the existing video
 * @param {Function} onProgress - Progress callback (0-100)
 * @returns {Promise<File>} - Compressed video file
 */
export const compressVideoFromUrl = async (videoUrl, onProgress) => {
  try {
    // Initialize FFmpeg if not already done
    const ff = await initFFmpeg(onProgress);

    const inputFileName = 'input_from_url.mp4';
    const outputFileName = 'output_low_res.mp4';

    // Fetch the video from URL
    const videoData = await fetchFile(videoUrl);
    await ff.writeFile(inputFileName, videoData);

    // Run FFmpeg compression command with optimized settings
    await ff.exec([
      '-i', inputFileName,
      '-c:v', 'libx264',
      '-preset', 'medium',
      '-b:v', '1500k',
      '-maxrate', '2000k',
      '-bufsize', '3000k',
      '-c:a', 'aac',
      '-b:a', '128k',
      '-movflags', '+faststart',
      '-y',
      outputFileName
    ]);

    // Read the output file
    const outputData = await ff.readFile(outputFileName);
    
    // Clean up
    await ff.deleteFile(inputFileName);
    await ff.deleteFile(outputFileName);

    // Create a new File from the compressed data
    const compressedBlob = new Blob([outputData], { type: 'video/mp4' });
    const compressedFile = new File(
      [compressedBlob], 
      generateFileName(),
      { type: 'video/mp4' }
    );

    return compressedFile;
  } catch (error) {
    console.error('Video compression from URL failed:', error);
    throw error;
  }
};

/**
 * Get compression status message
 */
export const getCompressionStatus = (progress) => {
  if (progress === 0) return 'Initializing...';
  if (progress < 30) return 'Processing video...';
  if (progress < 70) return 'Compressing...';
  if (progress < 100) return 'Finalizing...';
  return 'Complete!';
};
